import { getMyRoleRequests } from '../api/services/authService';

const DENY_MESSAGE = 'Access denied. Please select a role you have requested for';

export function mapUiRoleToAPI(uiRole) {
  const roleMap = {
    landlord: 'LANDLORD',
    'bnb-host': 'BNB_HOST',
    agent: 'AGENT',
    admin: 'ADMIN',
    tenant: 'TENANT',
    guest: 'GUEST',
  };
  return roleMap[uiRole] || String(uiRole).toUpperCase();
}

/**
 * Ensures the role chosen on the login screen matches the user's registered / approved role.
 * Tenant and guest are validated against JWT/login roles first, then role requests (same as other roles).
 */
export async function validateDashboardRoleAccess(selectedRole, options = {}) {
  const { userRoles = [], hasAdminRole = false, isAdminEmail = false } = options;

  try {
    const normalizedSelectedRole = String(selectedRole || '').trim().toLowerCase();

    if (normalizedSelectedRole === 'admin') {
      if (hasAdminRole || isAdminEmail) {
        return { hasAccess: true };
      }
      return {
        hasAccess: false,
        message: 'Access denied. Admin role is restricted.',
      };
    }

    const apiRole = mapUiRoleToAPI(normalizedSelectedRole);
    const normalizedRoles = (userRoles || []).map((r) => String(r).toUpperCase());

    // Tenant login should not be blocked by role-request records.
    // Some tenant accounts are valid in auth/profile but may not appear in role requests.
    if (apiRole === 'TENANT') {
      return { hasAccess: true };
    }

    // If JWT/login roles already contain the selected dashboard role, allow immediately.
    if (normalizedRoles.includes(apiRole)) {
      return { hasAccess: true };
    }

    if (normalizedSelectedRole === 'guest') {
      if (normalizedRoles.includes(apiRole)) {
        return { hasAccess: true };
      }
      if (normalizedRoles.length > 0) {
        return { hasAccess: false, message: DENY_MESSAGE };
      }
    }

    const roleRequestsResponse = await getMyRoleRequests();
    const roleRequests = roleRequestsResponse?.data || roleRequestsResponse || [];

    if (!Array.isArray(roleRequests) || roleRequests.length === 0) {
      return { hasAccess: false, message: DENY_MESSAGE };
    }

    const roleRequestsForSelectedRole = roleRequests.filter((req) => {
      const reqRole = (req?.role ?? req?.requestedRole ?? '').toUpperCase();
      return reqRole === apiRole;
    });

    if (roleRequestsForSelectedRole.length === 0) {
      return { hasAccess: false, message: DENY_MESSAGE };
    }

    // Allow access if *any* request for this role is approved/pending.
    const hasApprovedOrPending = roleRequestsForSelectedRole.some((req) => {
      const status = req?.status?.toUpperCase() || '';
      return status === 'APPROVED' || status === 'PENDING' || status === 'PENDING_REVIEW';
    });

    if (hasApprovedOrPending) return { hasAccess: true };

    // If we have requests but none are approved/pending, deny.
    return { hasAccess: false, message: DENY_MESSAGE };
  } catch (error) {
    console.error('Error checking role request:', error);
    return {
      hasAccess: false,
      message: 'Unable to verify role access. Please try again or contact support.',
    };
  }
}
